import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import CustomerLayout from "../../../layouts/CustomerLayout";
import contactRequests from "../../../api/Customer/ContactRequests";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

// Import all contact components
import {
  ContactHeader,
  ContactStatsCards,
  ContactGroupList,
  ContactList,
  ContactDeleteModal,
} from "../../../components/contacts";
import ContactAddModal from "../../../components/contacts/ContactAddModal";

const ContactGroup = () => {
  const navigate = useNavigate();

  // State management
  const [contactGroups, setContactGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All Groups");
  const [currentView, setCurrentView] = useState("groups");
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Date filter state (YYYY-MM-DD for API)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("group");
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalGroups: 0,
    recentUploads: 0,
    activeGroups: 0,
  });

  const location= useLocation()
  const loadContactGroups = useCallback(async (page = 1, perPage = 15, search = "", sortOrder = "desc", dateFromParam = "", dateToParam = "") => {
    try {
      setLoading(true);
      const response = await contactRequests.getContactGroups(
        page,
        perPage,
        search,
        "", // sortBy - empty for now
        sortOrder,
        dateFromParam,
        dateToParam
      );
      const { data } = response;

      if (data?.data?.status_code === "000" && !data?.data?.in_error) {
        const groups = data.data.data?.contact_groups || [];
        const paginationData = data.data.data?.pagination || {};
        
        setContactGroups(groups);
        setPagination({
          currentPage: paginationData.current_page || page,
          perPage: paginationData.per_page || perPage,
          total: paginationData.total || 0,
          lastPage: paginationData.last_page || 1,
          from: paginationData.from || 0,
          to: paginationData.to || 0,
        });

        // Calculate stats from current page data (or you might want to get this from backend)
        const totalContacts = groups.reduce(
          (sum, group) => sum + (group.number_of_contacts || 0),
          0
        );
        setStats({
          totalContacts,
          totalGroups: paginationData.total || groups.length,
          recentUploads: groups.filter((g) => {
            const isRecent =
              g.created_at &&
              new Date(g.created_at) >
                new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
            return isRecent;
          }).length,
          activeGroups: groups.filter((g) => (g.number_of_contacts || 0) > 0)
            .length,
        });
      } else {
        toast.error("Failed to load contact groups");
      }
    } catch (error) {
      toast.error("Error loading contact groups");
      console.error("Error loading contact groups:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search term for API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Handle search with debounce - reset to page 1 when search changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search changes
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Load contact groups on component mount and when pagination/search changes
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await loadContactGroups(
          pagination.currentPage,
          pagination.perPage,
          debouncedSearchTerm,
          "desc",
          dateFrom,
          dateTo
        );
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.perPage, debouncedSearchTerm, dateFrom, dateTo]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // Handle per page change
  const handlePerPageChange = (newPerPage) => {
    setPagination(prev => ({ ...prev, perPage: newPerPage, currentPage: 1 }));
  };

  // Handle date filter change - reset to page 1
  const handleDateFilterChange = (from, to) => {
    setDateFrom(from || "");
    setDateTo(to || "");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearDateFilters = () => {
    setDateFrom("");
    setDateTo("");
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  //Todo => Handle download template
  const handleDownloadTemplate = async () => {
    try {
      const link = document.createElement("a");
      link.href = "/docs/contact_template_format.xlsx";
      link.download = "contact_template_format.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Template downloaded successfully");
    } catch (error) {
      toast.error("Error downloading template");
      console.error("Error downloading template:", error);
    }
  };

  //Todo => Handle navigate to upload page
  const handleNavigateToUpload = () => {
   navigate("/contacts/upload", {
    state: { locationRoute: location.pathname}
  })
  };


  //Todo => Handle view group
  const handleViewGroup = async (group) => {
    setSelectedGroup(group);
    setCurrentView("contacts");
    // No need to load contacts since we're only showing export functionality
  };

  //Todo => Handle delete group
  const handleDeleteGroup = (group) => {
    setItemToDelete(group);
    setDeleteType("group");
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async (item) => {
    try {
      setDeleting(true);

      if (deleteType === "group") {
        const response = await contactRequests.deleteContactGroup(
          item.contact_group_slug
        );
        if (response.success) {
          toast.success("Group deleted successfully");
          setCurrentView("groups");
          setSelectedGroup(null);
          await loadContactGroups(pagination.currentPage, pagination.perPage, debouncedSearchTerm, "desc", dateFrom, dateTo);
        } else {
          toast.error("Failed to delete group");
        }
      }

      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error("Error deleting item");
      console.error("Delete error:", error);
    } finally {
      setDeleting(false);
    }
  };

  // Handle export group
  const handleExportGroup = async (groupName) => {
    try {
      // This would typically call an export API endpoint
      toast.info("Export functionality will be implemented");
    } catch (error) {
      toast.error("Error exporting group");
      console.error("Export error:", error);
    }
  };

  // Handle refresh group to check processing status
  const handleRefreshGroup = async (group) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading("Checking processing status...");
      await loadContactGroups(pagination.currentPage, pagination.perPage, debouncedSearchTerm, "desc", dateFrom, dateTo);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Check if processing is complete
      const updatedGroup = contactGroups.find(
        (g) => g.contact_group_slug === group.contact_group_slug
      );
      if (
        updatedGroup &&
        updatedGroup.uploaded_at &&
        updatedGroup.number_of_contacts !== null
      ) {
        toast.success(
          `✅ Processing complete! ${updatedGroup.number_of_contacts.toLocaleString()} contacts uploaded.`
        );
      } else {
        toast.info("Processing is still in progress. Check again in a moment.");
      }
    } catch (error) {
      toast.error("Error checking processing status");
      console.error("Refresh error:", error);
    }
  };

  const handleAddToGroup = (group) => {
    navigate(`/contacts/manage/${group.contact_group_slug}`);
    // //console.log("Navigating to add contacts to group:", group.contact_group_slug)
  };

  // //console.log("Contact Groups:", contactGroups);

  // Handle add contact success
  const handleAddContactSuccess = () => {
    // Refresh the groups list to update contact counts
    loadContactGroups(pagination.currentPage, pagination.perPage, debouncedSearchTerm, "desc", dateFrom, dateTo);
  };

  // Handle back to groups
  const handleBackToGroups = () => {
    setCurrentView("groups");
    setSelectedGroup(null);
  };

  return (
    <CustomerLayout>
      <div className="py-4 pt-16 min-h-screen bg-gray-50 sm:py-6 sm:pt-20">
        <div className="px-3 mx-auto max-w-8xl sm:px-4 md:px-6 lg:px-8">
          {/* Header Section */}
          <ContactHeader
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onUploadClick={handleNavigateToUpload}
            onDownloadTemplate={handleDownloadTemplate}
            totalContacts={stats.totalContacts}
            totalGroups={stats.totalGroups}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            filters={["All Groups", "Active", "Recent"]}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFilterChange={handleDateFilterChange}
            onClearDateFilters={handleClearDateFilters}
          />

          {currentView === "groups" && <ContactStatsCards stats={stats} />}

          {/* Main Content */}
          <motion.div
            key={currentView}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {currentView === "groups" ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Contact Groups List */}
                <ContactGroupList
                  groups={contactGroups}
                  loading={loading}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  onViewGroup={handleViewGroup}
                  onDownloadGroup={(group) =>
                    handleExportGroup(group.group_name)
                  }
                  onDeleteGroup={handleDeleteGroup}
                  onAddToGroup={handleAddToGroup}
                  onRefreshGroup={handleRefreshGroup}
                />

                {/* Help Section */}
                {/* {!loading && contactGroups.length > 0 && (
                  <ContactHelpSection
                    onDownloadTemplate={handleDownloadTemplate}
                  />
                )} */}
              </div>
            ) : (
              /* Contact List View */
              <ContactList
                groupName={selectedGroup?.group_name}
                groupSlug={selectedGroup?.contact_group_slug}
                onBack={handleBackToGroups}
                onExportGroup={handleExportGroup}
              />
            )}
          </motion.div>

          {/* Delete Modal */}
          <ContactDeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            item={itemToDelete}
            type={deleteType}
            isDeleting={deleting}
          />

          {/* Add Contact Modal */}
          <ContactAddModal
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false);
              setSelectedGroupForAdd(null);
            }}
            groupName={selectedGroupForAdd?.group_name}
            groupSlug={selectedGroupForAdd?.contact_group_slug}
            onSuccess={handleAddContactSuccess}
          />
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ContactGroup;
